import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { useDispatch, useSelector } from "react-redux";

import {
     lcvReportsClearDealersExport,
     lcvReportsGetDealersExportRequest,
     lcvReportsGetDealersRequest,
     lcvReportsGetFilterOptionsRequest,
} from "../../../../../_redux/_actions/lcvReports.action";
// Imported from their own file rather than the _constants barrel: the barrel can still be
// half-initialised when this module evaluates, which leaves the constants undefined.
import { lcvReportsConstants } from "../../../../../_redux/_constants/lcvReports.constants";
import ExportToExcelWithApi from "../../../../_uiComponents/common/ExportToExcelWithApi";
import useLcvCampaign from "../../shared/useLcvCampaign";

const ALL_OPTION = { id: null, name: "All" };

const ACTIVATION_OPTIONS = [
     { id: null, name: "All" },
     { id: true, name: "Activated" },
     { id: false, name: "Not activated" },
];

const INITIAL_FILTERS = {
     activation: ACTIVATION_OPTIONS[0],
     area: ALL_OPTION,
     dealer: ALL_OPTION,
};

const INITIAL_PAGE = { skip: 0, take: 25 };

const INITIAL_OPTION_SEARCH = { dealerSearch: "", areaSearch: "" };

// The download names the sheet after the report so several of them can sit in one folder.
const EXPORT_FILE_NAME = "lcv-dealer-report";

// Shown wherever a dealership carries no Pro+ status, which is every dealership that reached us on an
// order file rather than a participant file. A blank cell would read as a missing value instead.
const NO_PRO_PLUS_STATUS = "Not on participant file";

const LcvDealerReportGrid = () => {
     const actionDispatch = useDispatch();

     const dealerPage = useSelector((state) => state?.lcvReports?.dealerPage);
     const dealerExport = useSelector((state) => state?.lcvReports?.dealerExport);
     const filterOptions = useSelector((state) => state?.lcvReports?.filterOptions);

     // The read is in flight while its request action sits in the pending list the loading
     // middleware maintains. Used to mask the grid so a filter change never looks like it did nothing.
     const isLoading = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               lcvReportsConstants.LCV_REPORTS_GET_DEALERS_REQUEST,
          ),
     );

     const { selectedCampaignId } = useLcvCampaign();

     const [filters, setFilters] = useState(INITIAL_FILTERS);
     const [search, setSearch] = useState("");
     const [page, setPage] = useState(INITIAL_PAGE);
     const [sort, setSort] = useState([]);
     const [optionSearch, setOptionSearch] = useState(INITIAL_OPTION_SEARCH);

     // The dropdowns are searchable and the typed text is matched by the backend, so the option
     // lists are re-fetched rather than narrowed here.
     useEffect(() => {
          const timer = setTimeout(() => {
               actionDispatch(
                    lcvReportsGetFilterOptionsRequest({
                         dealerSearch: optionSearch.dealerSearch || undefined,
                         areaSearch: optionSearch.areaSearch || undefined,
                    }),
               );
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, optionSearch]);

     // Filtering, sorting and paging are all done by the backend: the request carries the criteria
     // and the response is exactly the page that should be shown.
     useEffect(() => {
          if (!selectedCampaignId) {
               return undefined;
          }

          const timer = setTimeout(() => {
               actionDispatch(lcvReportsGetDealersRequest(criteria(filters, search, sort, page)));
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, filters, search, sort, page, selectedCampaignId]);

     const areaOptions = useMemo(
          () => [ALL_OPTION, ...(filterOptions?.areas || [])],
          [filterOptions],
     );

     const dealerOptions = useMemo(
          () => [
               ALL_OPTION,
               ...(filterOptions?.dealers || []).map((dealer) => ({
                    id: dealer.id,
                    name: `${dealer.dealerCode} - ${dealer.name}`,
               })),
          ],
          [filterOptions],
     );

     const dealers = useMemo(
          () =>
               (dealerPage?.dealers || []).map((dealer) => ({
                    ...dealer,
                    proPlusStatus: dealer.proPlusStatus || NO_PRO_PLUS_STATUS,
               })),
          [dealerPage],
     );

     // The count is of rows rather than dealerships: a dealership with two managers returns two.
     const totalCount = dealerPage?.totalCount || 0;

     // The spreadsheet is built from whatever this hands over, so the columns are named and ordered
     // here. Anything left out never reaches the file, which is how the paging count and the internal
     // ids stay out of it.
     const exportRows = useMemo(
          () =>
               (dealerExport || []).map((dealer) => ({
                    Area: dealer.area,
                    "Dealer Code": dealer.dealerCode,
                    Dealer: dealer.dealerName,
                    "Pro+ Status": dealer.proPlusStatus || NO_PRO_PLUS_STATUS,
                    Manager: dealer.managerName,
                    "Manager Email": dealer.managerEmail,
                    Activated: dealer.managerActivated,
                    "Orders Placed": dealer.ordersPlaced,
                    "Sales To Date": dealer.salesToDate,
                    "Registrations To Next Code": dealer.registrationsToNextCode,
                    "Codes Issued": dealer.codesIssued,
                    "Codes Allocated": dealer.codesAllocated,
                    "Codes Played": dealer.codesPlayed,
               })),
          [dealerExport],
     );

     const onFilterChange = useCallback((field, value) => {
          setFilters((previous) => ({ ...previous, [field]: value }));
          setPage(INITIAL_PAGE);
     }, []);

     const onSearchChange = useCallback((event) => {
          setSearch(event.value);
          setPage(INITIAL_PAGE);
     }, []);

     const onOptionSearchChange = useCallback((field, event) => {
          setOptionSearch((previous) => ({
               ...previous,
               [field]: event.filter ? event.filter.value : "",
          }));
     }, []);

     const clearFilters = useCallback(() => {
          setFilters(INITIAL_FILTERS);
          setSearch("");
          setSort([]);
          setPage(INITIAL_PAGE);
          setOptionSearch(INITIAL_OPTION_SEARCH);
     }, []);

     // The file holds every row the filters match, not the page on screen, so the export request
     // carries the same criteria without the paging ones.
     const fetchForExport = useCallback(
          (onDownloadComplete) => {
               try {
                    actionDispatch(lcvReportsClearDealersExport());
                    actionDispatch(
                         lcvReportsGetDealersExportRequest(criteria(filters, search, sort)),
                    );
               } catch (error) {
                    onDownloadComplete(false);
               }
          },
          [actionDispatch, filters, search, sort],
     );

     return (
          <div className="sba-telerik-components-wr">
               <div
                    className="sba-chart-section-top-search"
                    style={{
                         display: "flex",
                         flexDirection: "row",
                         flexWrap: "wrap",
                         alignItems: "flex-end",
                         gap: "12px",
                         width: "100%",
                    }}
               >
                    <Input
                         style={{ width: "260px" }}
                         value={search}
                         onChange={onSearchChange}
                         placeholder="Search dealer code or name"
                    />
                    <DropDownList
                         style={{ width: "150px" }}
                         label="Area"
                         data={areaOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.area}
                         filterable={true}
                         onFilterChange={(event) => onOptionSearchChange("areaSearch", event)}
                         onChange={(event) => onFilterChange("area", event.value)}
                    />
                    <DropDownList
                         style={{ width: "280px" }}
                         label="Dealer"
                         data={dealerOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.dealer}
                         filterable={true}
                         onFilterChange={(event) => onOptionSearchChange("dealerSearch", event)}
                         onChange={(event) => onFilterChange("dealer", event.value)}
                    />
                    <DropDownList
                         style={{ width: "180px" }}
                         label="Activation"
                         data={ACTIVATION_OPTIONS}
                         textField="name"
                         dataItemKey="name"
                         value={filters.activation}
                         onChange={(event) => onFilterChange("activation", event.value)}
                    />
                    <Button type="button" onClick={clearFilters}>
                         Clear
                    </Button>
                    <ExportToExcelWithApi
                         name={EXPORT_FILE_NAME}
                         data={exportRows}
                         onDownloadClick={fetchForExport}
                    />
               </div>
               <div style={{ position: "relative" }}>
                    {isLoading && (
                         <div className="k-loading-mask" style={{ zIndex: 10 }}>
                              <span className="k-loading-text">Loading</span>
                              <div className="k-loading-image" />
                              <div className="k-loading-color" />
                         </div>
                    )}
                    <Grid
                         data={dealers}
                         skip={page.skip}
                         take={page.take}
                         total={totalCount}
                         sort={sort}
                         sortable={true}
                         className="sba-area-data-grid"
                         pageable={{
                              buttonCount: 4,
                              pageSizes: [10, 25, 50],
                         }}
                         onPageChange={(event) => setPage(event.page)}
                         onSortChange={(event) => {
                              setSort(event.sort);
                              setPage(INITIAL_PAGE);
                         }}
                    >
                         <GridColumn field="area" title="Area" />
                         <GridColumn field="dealerCode" title="Dealer Code" />
                         <GridColumn field="dealerName" title="Dealer" />
                         <GridColumn field="proPlusStatus" title="Pro+ Status" />
                         {/* One manager to a row. A dealership with several returns several rows and
                             repeats its own figures on each. */}
                         <GridColumn field="managerName" title="Manager" />
                         <GridColumn field="managerEmail" title="Manager Email" />
                         <GridColumn field="managerActivated" title="Activated" sortable={false} />
                         {/* Every order on the file, then the registered eligible ones that count
                             towards a code, then what is left of the next three. */}
                         <GridColumn field="ordersPlaced" title="Orders Placed" />
                         <GridColumn field="salesToDate" title="Sales To Date" />
                         <GridColumn field="registrationsToNextCode" title="Registrations To Next Code" />
                         <GridColumn field="codesIssued" title="Codes Issued" />
                         <GridColumn field="codesAllocated" title="Codes Allocated" />
                         <GridColumn field="codesPlayed" title="Codes Played" />
                    </Grid>
               </div>
          </div>
     );
};

// Paging is left out when no page is given, which is what the export route wants.
const criteria = (filters, search, sort, page) => {
     const payload = {
          areaId: filters.area.id,
          dealershipId: filters.dealer.id,
          accountActivated: filters.activation.id,
          searchTerm: search.trim() ? search.trim() : undefined,
          sortBy: sort.length ? sort[0].field : undefined,
          sortDirection: sort.length ? sort[0].dir : undefined,
     };

     if (!page) {
          return payload;
     }

     return {
          ...payload,
          page: Math.floor(page.skip / page.take) + 1,
          pageSize: page.take,
     };
};

export default LcvDealerReportGrid;
